<template>
  <van-swipe-cell :before-close="beforeClose" @open="onSwipeOpen" @close="onSwipeClose" @touchmove="onSwipeTouchMove"
                  @touchstart="onSwipeTouchStart" @touchend="onSwipeTouchEnd" :disabled="$device.isDesktop">
    <!-- Right Swipe Wishlist -->
    <template #right>
      <div class="h-full bg-[#F8B5BE] relative" :style="{ width: width + 'px' }">
        <div ref="wishlistIcon"
             class="absolute top-1/2 transform -translate-y-1/2 left-10 flex flex-col flex-center gap-2 transition-all ease-in-out duration-300"
             :class="{ 'left-1/2 translate-x-[-50%]': isSwiping('left') }">
          <niceone-image :src="$publicPath('/images/cart-buttons/new-heart-icon.svg')" width="24"/>
          <div class="text-xs">{{ $t('cart.move_to_wishlist') }}</div>
        </div>
      </div>
    </template>

    <!-- Main Content -->
    <slot/>

    <!-- Left Swipe Delete -->
    <template #left>
      <div class="h-full bg-[#EE2D64] relative" :style="{ width: width + 'px' }">
        <div ref="deleteIcon"
             class="absolute top-1/2 transform -translate-y-1/2 right-10 flex flex-col flex-center gap-2 transition-all ease-in-out duration-300"
             :class="{ 'right-1/2 translate-x-[50%]': isSwiping('right') }">
          <niceone-image :src="$publicPath('/images/cart-buttons/white-remove.svg')" width="24"/>
          <div class="text-xs text-white">{{ $t('common.delete') }}</div>
        </div>
      </div>
    </template>
  </van-swipe-cell>

  <!-- Delete Confirmation Popup -->
  <delete-popup v-if="savePosition=='left'" v-model="showDeletePopup" :title="$t('cart.wishlist_alert_message')"
  :confirmText="$t('common.yes')" :cancelText="$t('common.no')"  @cancel="onCancel"
  @delete="onUpdateCart"/>
<delete-popup v-if="savePosition=='right'" v-model="showDeletePopup"  @cancel="onCancel"
  @delete="onUpdateCart"/>
</template>

<script setup>
import {useEventBus, useWindowSize} from "@vueuse/core";
import {EVENTS} from "@/constants/events";
import DeletePopup from "@/components/cart/DeletePopup";

// Emits
const emit = defineEmits(["onRemove", "AddToWishList"]);

// Props
const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
});

// Get window width
const {width} = useWindowSize();

// Event Bus for Delete Dialog
const {on} = useEventBus(EVENTS.DELETE_DIALOG);
on(() => onCancel());

// Swipe State
const beforeClose = ref(false);
const savePosition = ref("cell");
const showDeletePopup = ref(false);
const swipeStartX = ref(0);
const swipeDistance = ref(0);
const threshold = 200;
const fastSwipeThreshold = 50;

// Dynamic Swipe State
const swipeState = ref(null);

const {t} = useI18n();

// Computed Property for Swipe Direction
const isSwiping = (direction) => {
  if (direction === "right") return swipeState.value === "right" || swipeDistance.value >= threshold;
  if (direction === "left") return swipeState.value === "left" || swipeDistance.value <= -threshold;
  return false;
};


const preventOnDesktop = () => useNuxtApp().$device.isMobile;
// Swipe Open Handler
const onSwipeOpen = ({position}) => {
  if (["left", "right"].includes(position)) {
    swipeState.value = position;
    savePosition.value = position;
    showDeletePopup.value = true;
  } else {
    beforeClose.value = false;
  }
};

// Handle Cart Update
const onUpdateCart = () => {
  if (savePosition.value === "left") {
    emit("AddToWishList", {key: props?.product?.key, product_id: props?.product?.product_id});
  } else if (savePosition.value === "right") {
    emit("onRemove", {key: props?.product?.key});

  }
  onCancel();
};

// Handle Cancel Action
const onCancel = () => {
  showDeletePopup.value = false;
  beforeClose.value = false;
  savePosition.value = "cell";
  swipeDistance.value = 0;
  swipeState.value = null;
};

// Touch Start Handler
const onSwipeTouchStart = (event) => {
  if (!preventOnDesktop()) return;
  swipeStartX.value = event.touches[0].clientX;
  swipeState.value = null;
};

// Touch Move Handler
const onSwipeTouchMove = (event) => {
  if (!preventOnDesktop()) return;
  swipeDistance.value = event.touches[0].clientX - swipeStartX.value;
};

// Touch End Handler for Fast Swipe Detection
const onSwipeTouchEnd = () => {
  if (!preventOnDesktop()) return;
  if (swipeDistance.value >= fastSwipeThreshold) {
    onSwipeOpen({position: "right"});
  } else if (swipeDistance.value <= -fastSwipeThreshold) {
    onSwipeOpen({position: "left"});
  }
  swipeDistance.value = 0;
};

// Reset Swipe on Close
const onSwipeClose = () => {
  if (!preventOnDesktop()) return;
  swipeDistance.value = 0;
  swipeState.value = null;
};
</script>